import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Textarea component with automatic uppercase transformation
 * 
 * Features:
 * - Transforms input to UPPERCASE and stores the value in uppercase
 * - Preserves cursor position while typing (no cursor jump)
 * - Manual opt-out: Set uppercase={false} to disable
 * 
 * Usage:
 *   <Textarea />                    // Uppercase enabled (default)
 *   <Textarea uppercase={false} />  // Uppercase disabled
 */
const Textarea = React.forwardRef(({ className, uppercase = true, onChange, style, ...props }, ref) => {
  // Handle input change with cursor-preserving uppercase transformation
  const handleChange = (e) => {
    if (uppercase) {
      const textarea = e.target;
      const cursorStart = textarea.selectionStart;
      const cursorEnd = textarea.selectionEnd;
      
      // Transform to uppercase
      const upperValue = textarea.value.toUpperCase();
      
      // Only update if value changed (prevents unnecessary cursor manipulation)
      if (textarea.value !== upperValue) {
        textarea.value = upperValue;
        // Restore cursor position after transformation
        textarea.setSelectionRange(cursorStart, cursorEnd);
      }
    }
    
    // Call the original onChange handler
    onChange?.(e);
  };

  return (
    <textarea
      className={cn(
        "flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      onChange={handleChange}
      style={style}
      {...props} />
  );
})
Textarea.displayName = "Textarea"

export { Textarea }
